import {memo, type ReactNode} from 'react'
import {ChevronDown, Check} from 'lucide-react'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/shared/ui'
import {cn} from '@/shared/lib/cn'

const toggleBase =
  'inline-flex items-center justify-center cursor-pointer text-foreground backdrop-blur-[10px] transition-all duration-200 ease-out hover:scale-105 active:scale-95 disabled:opacity-50 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0'

const variantClasses = {
  default: 'bg-foreground/15 hover:bg-foreground/25',
  muted: 'bg-destructive hover:bg-destructive/80',
} as const

const sizeClasses = {
  small: "size-10 [&_svg:not([class*='size-'])]:size-[18px]",
  medium: "size-13 [&_svg:not([class*='size-'])]:size-6",
} as const

interface MediaToggleSplitButtonProps {
  isEnabled: boolean
  onToggle: () => void
  devices: MediaDeviceInfo[]
  selectedDeviceId: string | null
  onSelectDevice: (deviceId: string) => void
  icon: ReactNode
  disabledIcon: ReactNode
  toggleLabel: string
  pickerLabel: string
  /** Used as fallback name when a device has no label, e.g. "Камера" → "Камера 1" */
  deviceFallbackName?: string
  disabled?: boolean
  size?: 'small' | 'medium'
}

export const MediaToggleSplitButton = memo(function MediaToggleSplitButton({
  isEnabled,
  onToggle,
  devices,
  selectedDeviceId,
  onSelectDevice,
  icon,
  disabledIcon,
  toggleLabel,
  pickerLabel,
  deviceFallbackName = 'Устройство',
  disabled = false,
  size = 'small',
}: MediaToggleSplitButtonProps) {
  const variant = isEnabled ? 'default' : 'muted'
  const iconSize = sizeClasses[size]
  const pickerDisabled = disabled || devices.length === 0

  const pickerHeight = size === 'small' ? 'h-10' : 'h-13'
  const pickerWidth = size === 'small' ? 'w-8' : 'w-9'

  return (
    <div className={cn('flex items-stretch gap-0 overflow-hidden rounded-full')}>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-label={toggleLabel}
        title={toggleLabel}
        className={cn(
          toggleBase,
          variantClasses[variant],
          iconSize,
          'rounded-l-full rounded-r-none shrink-0 border-0 outline-none',
        )}
      >
        {isEnabled ? icon : disabledIcon}
      </button>
      <span className="w-px min-w-0 shrink-0 self-stretch bg-foreground/20" aria-hidden />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={pickerDisabled}
            aria-label={pickerLabel}
            title={pickerLabel}
            className={cn(
              toggleBase,
              variantClasses[variant],
              pickerHeight,
              pickerWidth,
              'rounded-l-none rounded-r-full shrink-0 border-0 min-w-0 outline-none',
              size === 'small' && "[&_svg:not([class*='size-'])]:size-[14px]",
              size === 'medium' && "[&_svg:not([class*='size-'])]:size-4",
            )}
          >
            <ChevronDown />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {devices.length === 0 ? (
            <DropdownMenuItem disabled onSelect={e => e.preventDefault()}>
              Нет устройств
            </DropdownMenuItem>
          ) : (
            devices.map((device, index) => (
              <DropdownMenuItem
                key={device.deviceId || index}
                onSelect={() => onSelectDevice(device.deviceId)}
                disabled={selectedDeviceId === device.deviceId}
                className="cursor-pointer"
              >
                <span className="flex-1 truncate">
                  {device.label || `${deviceFallbackName} ${index + 1}`}
                </span>
                {selectedDeviceId === device.deviceId && <Check className="size-4 shrink-0 ml-2" />}
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
})
